import React, { useState } from "react";
import { PublicPage, PageIntro, GREEN, GOLD, INK } from "../../components/public/PublicChrome";
import { api } from "../../lib/api";

const TIPOS = [
  { value: 39, label: "Boleta electrónica" },
  { value: 41, label: "Boleta exenta electrónica" },
];

const INPUT = "w-full rounded-sm border bg-white px-3 py-2.5 text-[15px] outline-none focus:ring-2";

async function mensajeError(e) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()).detail || "No se pudo verificar la boleta.";
    } catch {
      return "No se pudo verificar la boleta.";
    }
  }
  return "No se pudo conectar. Intente nuevamente.";
}

export default function VerificarBoleta() {
  const [form, setForm] = useState({ rut_emisor: "", tipo: 39, folio: "", fecha: "", monto: "" });
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState("");
  const [pdf, setPdf] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const verificar = async (e) => {
    e.preventDefault();
    setError("");
    if (pdf) URL.revokeObjectURL(pdf);
    setPdf(null);
    setBuscando(true);
    try {
      const r = await api.post(
        "/verificar-boleta",
        {
          rut_emisor: form.rut_emisor.trim(),
          tipo: Number(form.tipo),
          folio: Number(form.folio),
          fecha: form.fecha,
          monto: Number(String(form.monto).replace(/\D/g, "")),
        },
        { responseType: "blob" },
      );
      setPdf(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })));
    } catch (err) {
      setError(await mensajeError(err));
    } finally {
      setBuscando(false);
    }
  };

  const label = "block text-[12px] font-semibold tracking-[0.12em] uppercase mb-1.5";
  const borde = { borderColor: `${GREEN}33`, "--tw-ring-color": `${GOLD}66` };

  return (
    <PublicPage title="Verificar boleta" description="Consulte y descargue su boleta electrónica.">
      <PageIntro eyebrow="Boleta electrónica" title="Verificar documento">
        <p>Ingrese los datos impresos en su boleta para consultarla y descargar su representación impresa.</p>
      </PageIntro>

      <section className="max-w-3xl mx-auto px-6 sm:px-8 py-12 md:py-14" data-testid="verificar-boleta">
        <form onSubmit={verificar} className="grid sm:grid-cols-2 gap-5" style={{ color: INK }}>
          <div>
            <label className={label} style={{ color: GREEN }} htmlFor="vb-rut">RUT del emisor</label>
            <input id="vb-rut" className={INPUT} style={borde} required placeholder="76.123.456-7"
              value={form.rut_emisor} onChange={set("rut_emisor")} />
          </div>
          <div>
            <label className={label} style={{ color: GREEN }} htmlFor="vb-tipo">Tipo de documento</label>
            <select id="vb-tipo" className={INPUT} style={borde} value={form.tipo} onChange={set("tipo")}>
              {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className={label} style={{ color: GREEN }} htmlFor="vb-folio">Folio</label>
            <input id="vb-folio" className={INPUT} style={borde} required inputMode="numeric" pattern="\d+"
              value={form.folio} onChange={set("folio")} />
          </div>
          <div>
            <label className={label} style={{ color: GREEN }} htmlFor="vb-fecha">Fecha de emisión</label>
            <input id="vb-fecha" type="date" className={INPUT} style={borde} required
              value={form.fecha} onChange={set("fecha")} />
          </div>
          <div>
            <label className={label} style={{ color: GREEN }} htmlFor="vb-monto">Monto total ($)</label>
            <input id="vb-monto" className={INPUT} style={borde} required inputMode="numeric" placeholder="9.000"
              value={form.monto} onChange={set("monto")} />
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={buscando}
              className="w-full rounded-sm px-5 py-2.5 text-[14px] font-semibold tracking-wide text-white disabled:opacity-60"
              style={{ backgroundColor: GREEN }}>
              {buscando ? "Verificando…" : "Verificar boleta"}
            </button>
          </div>
        </form>

        {error && (
          <p className="mt-6 rounded-sm border px-4 py-3 text-[14px]" role="alert"
            style={{ borderColor: "#b4532a55", backgroundColor: "#b4532a0d", color: "#8a3a1a" }}>
            {error}
          </p>
        )}

        {pdf && (
          <div className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <p className="text-[15px] font-semibold" style={{ color: GREEN }}>Boleta encontrada</p>
              <a href={pdf} download={`boleta_${form.tipo}_${form.folio}.pdf`}
                className="text-[14px] font-semibold underline" style={{ color: GOLD }}>
                Descargar PDF
              </a>
            </div>
            <iframe title="Boleta" src={pdf} className="w-full h-[640px] rounded-sm border"
              style={{ borderColor: `${GREEN}22` }} />
          </div>
        )}

        <p className="mt-10 text-[13px] leading-relaxed" style={{ color: `${INK}99` }}>
          Los datos deben coincidir exactamente con los impresos en la boleta. La validez tributaria del documento
          también puede consultarse en el sitio del Servicio de Impuestos Internos (www.sii.cl).
        </p>
      </section>
    </PublicPage>
  );
}
